import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getSharedNutritionPlan } from '../services/nutritionService';
import './SharedNutritionPlan.css';

const SharedNutritionPlan = () => {
  const { token } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        setLoading(true);
        const data = await getSharedNutritionPlan(token);
        setPlan(data);
      } catch (err) {
        setError(err.message || 'Failed to load nutrition plan');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchPlan();
    }
  }, [token]);
  
  if (loading) {
    return (
      <div className="shared-nutrition-plan loading">
        <div className="loading-content">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading nutrition plan...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="shared-nutrition-plan error">
        <div className="error-content">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>Plan Not Found</h2>
          <p>{error}</p>
          <Link to="/nutrition" className="create-own-btn">
            <i className="fas fa-calculator"></i> Create Your Own Plan
          </Link>
        </div>
      </div>
    );
  }
  
  if (!plan) {
    return null;
  }
  
  const goalTypeLabels = {
    bulk: { label: 'Muscle Building', icon: 'fa-dumbbell', color: '#10b981' },
    maintain: { label: 'Maintenance', icon: 'fa-balance-scale', color: '#3b82f6' },
    cut: { label: 'Fat Loss', icon: 'fa-fire', color: '#ef4444' },
    custom: { label: 'Custom', icon: 'fa-sliders-h', color: '#8b5cf6' }
  };
  
  const goalInfo = goalTypeLabels[plan.goalType] || goalTypeLabels.custom;
  
  return (
    <div className="shared-nutrition-plan">
      <div className="shared-header">
        <div className="header-badge">
          <i className="fas fa-share-alt"></i> Shared Nutrition Plan
        </div>
        <h1>{plan.name}</h1>
        <div className="goal-badge" style={{ backgroundColor: goalInfo.color }}>
          <i className={`fas ${goalInfo.icon}`}></i>
          {goalInfo.label}
        </div>
        <p className="plan-meta">
          {plan.mealsPerDay} meals per day · Created {new Date(plan.createdAt).toLocaleDateString()}
        </p>
      </div>
      
      <div className="shared-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <i className="fas fa-chart-pie"></i> Nutrition Targets
        </button>
        {plan.weekPlan && plan.weekPlan.days && plan.weekPlan.days.length > 0 && (
          <button 
            className={`tab-btn ${activeTab === 'mealplan' ? 'active' : ''}`}
            onClick={() => setActiveTab('mealplan')}
          >
            <i className="fas fa-calendar-week"></i> Week Plan
          </button>
        )}
      </div>
      
      {activeTab === 'overview' && (
        <div className="shared-content">
          <section className="daily-targets-section">
            <h2><i className="fas fa-bullseye"></i> Daily Targets</h2>
            <div className="targets-grid">
              <div className="target-card calories">
                <div className="target-icon">
                  <i className="fas fa-fire-alt"></i>
                </div>
                <div className="target-info">
                  <span className="target-value">{plan.dailyTargets.dailyCalories}</span>
                  <span className="target-label">Calories</span>
                </div>
              </div>
              
              <div className="target-card protein">
                <div className="target-icon">
                  <i className="fas fa-drumstick-bite"></i>
                </div>
                <div className="target-info">
                  <span className="target-value">{plan.dailyTargets.dailyProtein}g</span>
                  <span className="target-label">Protein</span>
                  <span className="target-range">
                    ({plan.dailyTargets.dailyProteinMin}-{plan.dailyTargets.dailyProteinMax}g range)
                  </span>
                </div>
              </div>
              
              <div className="target-card carbs">
                <div className="target-icon">
                  <i className="fas fa-bread-slice"></i>
                </div>
                <div className="target-info">
                  <span className="target-value">{plan.dailyTargets.dailyCarbs}g</span>
                  <span className="target-label">Carbohydrates</span>
                </div>
              </div>
              
              <div className="target-card fat">
                <div className="target-icon">
                  <i className="fas fa-cheese"></i>
                </div>
                <div className="target-info">
                  <span className="target-value">{plan.dailyTargets.dailyFat}g</span>
                  <span className="target-label">Fat</span>
                </div>
              </div>
            </div>
          </section>
          
          <section className="per-meal-section">
            <h2><i className="fas fa-utensils"></i> Per-Meal Targets</h2>
            <p className="section-subtitle">
              With {plan.mealsPerDay} meals per day, aim for these targets at each meal
            </p>
            <div className="per-meal-cards">
              <div className="per-meal-card highlight">
                <span className="per-meal-value">{plan.perMealTargets.perMealProtein}g</span>
                <span className="per-meal-label">Protein</span>
                <span className="per-meal-note">Priority target</span>
              </div>
              <div className="per-meal-card">
                <span className="per-meal-value">{plan.perMealTargets.perMealCalories}</span>
                <span className="per-meal-label">Calories</span>
              </div>
              <div className="per-meal-card">
                <span className="per-meal-value">{plan.perMealTargets.perMealCarbs}g</span>
                <span className="per-meal-label">Carbs</span>
              </div>
              <div className="per-meal-card">
                <span className="per-meal-value">{plan.perMealTargets.perMealFat}g</span>
                <span className="per-meal-label">Fat</span>
              </div>
            </div>
          </section>
          
          <section className="macro-split-section">
            <h2><i className="fas fa-chart-pie"></i> Macro Split</h2>
            <div className="macro-visual">
              <div className="macro-bars">
                <div className="macro-bar-item">
                  <div className="bar-label">Protein</div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill protein"
                      style={{ width: `${plan.dailyTargets.macroPercentages.protein}%` }}
                    ></div>
                  </div>
                  <div className="bar-value">{plan.dailyTargets.macroPercentages.protein}%</div>
                </div>
                <div className="macro-bar-item">
                  <div className="bar-label">Carbs</div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill carbs"
                      style={{ width: `${plan.dailyTargets.macroPercentages.carbs}%` }}
                    ></div>
                  </div>
                  <div className="bar-value">{plan.dailyTargets.macroPercentages.carbs}%</div>
                </div>
                <div className="macro-bar-item">
                  <div className="bar-label">Fat</div>
                  <div className="bar-container">
                    <div 
                      className="bar-fill fat"
                      style={{ width: `${plan.dailyTargets.macroPercentages.fat}%` }}
                    ></div>
                  </div>
                  <div className="bar-value">{plan.dailyTargets.macroPercentages.fat}%</div>
                </div>
              </div>
            </div>
          </section>
          
          {plan.notes && (
            <section className="notes-section">
              <h2><i className="fas fa-sticky-note"></i> Coach Notes</h2>
              <div className="notes-content">
                {plan.notes}
              </div>
            </section>
          )}
        </div>
      )}
      
      {activeTab === 'mealplan' && plan.weekPlan && plan.weekPlan.days && (
        <div className="shared-content">
          <section className="week-plan-section">
            <h2><i className="fas fa-calendar-week"></i> Your Week Plan</h2>
            <div className="week-days-grid">
              {plan.weekPlan.days.map((day) => (
                <div key={day.dayNumber} className="day-card">
                  <div className="day-header">
                    <span className="day-name">{day.dayName || `Day ${day.dayNumber}`}</span>
                    {day.date && (
                      <span className="day-date">{new Date(day.date).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="day-meals">
                    {['breakfast', 'lunch', 'dinner', 'snacks'].map(mealType => {
                      const meal = day.meals?.[mealType];
                      const hasItems = (meal?.foods?.length > 0) || (meal?.menuItems?.length > 0);
                      if (!hasItems) return null;
                      
                      return (
                        <div key={mealType} className="meal-section">
                          <h4 className="meal-type-label">
                            {mealType.charAt(0).toUpperCase() + mealType.slice(1)}
                          </h4>
                          <ul className="meal-items-list">
                            {meal.menuItems?.map((item, idx) => (
                              <li key={`menu-${idx}`} className="meal-item recipe">
                                <i className="fas fa-book-open"></i>
                                <span>{item.title || 'Recipe'}</span>
                                {item.servings > 1 && <span className="servings">×{item.servings}</span>}
                              </li>
                            ))}
                            {meal.foods?.map((item, idx) => (
                              <li key={`food-${idx}`} className="meal-item food">
                                <i className="fas fa-apple-whole"></i>
                                <span>{item.description || 'Food'}</span>
                                {item.quantity > 1 && <span className="quantity">×{item.quantity}</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}
      
      <div className="shared-footer">
        <div className="powered-by">
          <span>Powered by</span>
          <Link to="/">FreshFitLab</Link>
        </div>
      </div>
    </div>
  );
};

export default SharedNutritionPlan;
